import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  loadApplicantCoachReplyLanguage,
  queryApplicantCoach as queryApplicantCoachApi,
} from "../../services/applicant/applicantCoachApi";

const ApplicantCoachContext = createContext(null);

function normalizeCoachMessage(reply, question, quickAction, replyLanguage) {
  if (!reply) return null;
  return {
    id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    question: question || "",
    quick_action: quickAction || null,
    answer: reply.answer || "",
    recommended_next_step: reply.recommended_next_step || "",
    caution: reply.caution || "",
    source_mode: reply.source_mode || "fallback",
    reply_language: replyLanguage?.code || reply.reply_language || "en",
    reply_direction: replyLanguage?.direction || "ltr",
    created_at: new Date().toISOString(),
  };
}

function buildCoachContextSignature({ routePath, screenKey, focusedOpportunityId, highlightedStep, surfaceStateKey }) {
  return JSON.stringify({
    route_path: routePath || "",
    screen_key: screenKey || "",
    focused_opportunity_id: focusedOpportunityId ?? null,
    highlighted_step: highlightedStep || "",
    surface_state_key: surfaceStateKey || "",
  });
}

export function ApplicantCoachProvider({ children }) {
  const location = useLocation();
  const routePath = `${location.pathname}${location.search}`;
  const [coachOpen, setCoachOpen] = useState(false);
  const [coachLoading, setCoachLoading] = useState(false);
  const [coachError, setCoachError] = useState("");
  const [coachSurfaceContext, setCoachSurfaceContext] = useState({});
  const [coachMessages, setCoachMessages] = useState([]);
  const [replyLanguage, setReplyLanguage] = useState({
    code: "en",
    label: "English",
    direction: "ltr",
    source: "fallback",
  });
  const coachRequestSeqRef = useRef(0);
  const coachContextSignatureRef = useRef("");
  const pendingSurfaceSignatureRef = useRef("");

  const coachContextSignature = useMemo(() => buildCoachContextSignature({
    routePath,
    screenKey: coachSurfaceContext?.screen_key,
    focusedOpportunityId: coachSurfaceContext?.focused_opportunity_id,
    highlightedStep: coachSurfaceContext?.highlighted_step,
    surfaceStateKey: coachSurfaceContext?.surface_state_key,
  }), [
    routePath,
    coachSurfaceContext?.screen_key,
    coachSurfaceContext?.focused_opportunity_id,
    coachSurfaceContext?.highlighted_step,
    coachSurfaceContext?.surface_state_key,
  ]);

  useEffect(() => {
    coachContextSignatureRef.current = coachContextSignature;
    setCoachError("");
    const isPendingFocusedRequest = (
      pendingSurfaceSignatureRef.current
      && pendingSurfaceSignatureRef.current === coachContextSignature
    );
    if (isPendingFocusedRequest) {
      return;
    }
    pendingSurfaceSignatureRef.current = "";
    coachRequestSeqRef.current += 1;
    setCoachLoading(false);
    setCoachMessages([]);
  }, [coachContextSignature]);

  const registerCoachSurface = useCallback((surface) => {
    setCoachSurfaceContext({
      route_path: routePath,
      ...(surface || {}),
    });
  }, [routePath]);

  const syncReplyLanguage = useCallback((nextLanguage) => {
    if (!nextLanguage?.code) return;
    setReplyLanguage((current) => ({
      ...current,
      ...nextLanguage,
      code: nextLanguage.code || current.code,
      label: nextLanguage.label || current.label,
      direction: nextLanguage.direction || current.direction,
      source: nextLanguage.source || current.source,
    }));
  }, []);

  useEffect(() => {
    let ignore = false;

    async function syncInitialReplyLanguage() {
      try {
        const nextLanguage = await loadApplicantCoachReplyLanguage();
        if (!ignore && nextLanguage?.code) {
          syncReplyLanguage(nextLanguage);
        }
      } catch (_error) {
        // Ohne geladene Präferenz bleibt der Coach auf dem lokalen Fallback.
      }
    }

    syncInitialReplyLanguage();
    return () => {
      ignore = true;
    };
  }, [syncReplyLanguage]);

  const askApplicantCoach = useCallback(async ({ question, quickAction, surfaceContext: surfaceContextOverride }) => {
    const surfaceContext = {
      route_path: routePath,
      ...(coachSurfaceContext || {}),
      ...(surfaceContextOverride || {}),
    };
    const signatureAtRequest = buildCoachContextSignature({
      routePath: surfaceContext.route_path,
      screenKey: surfaceContext.screen_key,
      focusedOpportunityId: surfaceContext.focused_opportunity_id,
      highlightedStep: surfaceContext.highlighted_step,
      surfaceStateKey: surfaceContext.surface_state_key,
    });
    const requestSeq = coachRequestSeqRef.current + 1;
    coachRequestSeqRef.current = requestSeq;

    if (!surfaceContext.screen_key) {
      setCoachError("Der Coach braucht zuerst einen stabilen sichtbaren Applicant-Kontext.");
      return null;
    }

    try {
      pendingSurfaceSignatureRef.current = surfaceContextOverride ? signatureAtRequest : "";
      if (surfaceContextOverride) {
        setCoachMessages([]);
      }
      setCoachLoading(true);
      setCoachError("");
      const result = await queryApplicantCoachApi({
        question,
        quickAction,
        surfaceContext,
      });

      const contextStillCurrent = (
        coachRequestSeqRef.current === requestSeq
        && (
          pendingSurfaceSignatureRef.current === signatureAtRequest
          || coachContextSignatureRef.current === signatureAtRequest
        )
      );
      if (!contextStillCurrent) return null;

      if (result?.coachContext || surfaceContextOverride) {
        setCoachSurfaceContext({
          ...surfaceContext,
          ...(result?.coachContext || {}),
        });
      }

      if (result?.replyLanguage?.code) {
        syncReplyLanguage(result.replyLanguage);
      }

      const message = normalizeCoachMessage(result?.reply, question, quickAction, result?.replyLanguage || replyLanguage);
      if (message) {
        setCoachMessages((current) => [...current, message]);
      }
      pendingSurfaceSignatureRef.current = "";
      return result?.reply || null;
    } catch (err) {
      if (coachRequestSeqRef.current === requestSeq) {
          setCoachError(err.response?.data?.message || "Der KI/AI Coach konnte nicht antworten.");
      }
      pendingSurfaceSignatureRef.current = "";
      return null;
    } finally {
      const requestStillCurrent = (
        coachRequestSeqRef.current === requestSeq
        || coachContextSignatureRef.current === signatureAtRequest
      );
      if (requestStillCurrent) {
        setCoachLoading(false);
      }
    }
  }, [coachSurfaceContext, replyLanguage, routePath, syncReplyLanguage]);

  const value = useMemo(() => ({
    coachOpen,
    setCoachOpen,
    coachLoading,
    coachError,
    coachMessages,
    coachSurfaceContext,
    registerCoachSurface,
    askApplicantCoach,
    replyLanguage,
    syncReplyLanguage,
  }), [
    coachOpen,
    coachLoading,
    coachError,
    coachMessages,
    coachSurfaceContext,
    registerCoachSurface,
    askApplicantCoach,
    replyLanguage,
    syncReplyLanguage,
  ]);

  return (
    <ApplicantCoachContext.Provider value={value}>
      {children}
    </ApplicantCoachContext.Provider>
  );
}

export function useApplicantCoach() {
  const context = useContext(ApplicantCoachContext);
  if (!context) {
    throw new Error("useApplicantCoach must be used within ApplicantCoachProvider");
  }
  return context;
}
